import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import {
    KeyboardDatePicker,
    KeyboardDateTimePicker,
} from '@material-ui/pickers'
import { DateTime } from 'luxon'
import { toast } from 'react-toastify'

import { useCreateLeadWithTour } from '../../hooks/Outreach/useCreateLeadWithTour'
import { useGuestCards } from '../../hooks/Outreach/useGuestCards'
import { GuestCardFormRequest } from '../../hooks/Outreach/useGuestCardForm'
import { useSubmitLeadGuestCard } from '../../hooks/Outreach/useSubmitLeadGuestCard'
import { useUser } from '../../hooks/useUser'
import { useAptConfig } from '../../hooks/useAptConfig'
import { GuestCard } from '../../models/Outreach/types'
import { GuestCardFormFields } from '../Outbound/Leads/ProspectsTab/GuestCardFormFields'

const useStyles = makeStyles((theme: Theme) => ({
    dialogPaper: {
        borderRadius: 16,
        maxWidth: 560,
        width: '100%',
    },
    dialogPaperWide: {
        borderRadius: 16,
        width: 'min(720px, 92vw)',
        maxWidth: '92vw',
        height: 'min(860px, 88vh)',
        maxHeight: '88vh',
        display: 'flex',
        flexDirection: 'column',
    },
    title: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
    },
    content: {
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5),
        overflowY: 'auto',
        flex: 1,
        minHeight: 0,
    },
    row: {
        display: 'flex',
        gap: theme.spacing(2),
        '& > *': { flex: 1 },
    },
    actions: {
        padding: theme.spacing(2, 3),
        borderTop: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
    },
    guestCardToggle: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 12,
        padding: theme.spacing(1.5, 2),
        backgroundColor: '#fafafa',
    },
    pickerCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 12,
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        backgroundColor: '#fafafa',
    },
    formControl: {
        minWidth: '100%',
        marginTop: theme.spacing(1.25),
        '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#fff',
        },
    },
    stepHint: {
        fontSize: '0.8rem',
        color: theme.palette.text.secondary,
        marginTop: 4,
    },
}))

interface Props {
    open: boolean
    onClose: () => void
    initialDate?: DateTime | null
}

type Step = 'tour' | 'guest_card'

export const ScheduleTourModal: React.FC<Props> = ({
    open,
    onClose,
    initialDate,
}) => {
    const classes = useStyles()
    const { createLeadWithTour, isCreating } = useCreateLeadWithTour()
    const {
        submitLeadGuestCard,
        isSubmitting,
        reset: resetGuestCardSubmit,
    } = useSubmitLeadGuestCard()
    const { workspaceUser, userList } = useUser()
    const { unitConfigList } = useAptConfig({ unitConfigList: true })
    const { guestCards, isLoadingGuestCards } = useGuestCards()

    const [step, setStep] = useState<Step>('tour')
    const [includeGuestCard, setIncludeGuestCard] = useState(false)

    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [startsAt, setStartsAt] = useState<Date | null>(null)
    const [givenById, setGivenById] = useState<number | ''>('')
    const [notes, setNotes] = useState('')
    const [preferredUnitConfig, setPreferredUnitConfig] = useState<number | ''>(
        '',
    )
    const [preferredMoveIn, setPreferredMoveIn] = useState<Date | null>(null)
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

    const [selectedGuestCardId, setSelectedGuestCardId] = useState<number | ''>(
        '',
    )
    const [formKey, setFormKey] = useState(0)

    const isBusy = isCreating || isSubmitting

    useEffect(() => {
        if (!open) return
        setStep('tour')
        setIncludeGuestCard(false)
        setName('')
        setEmail('')
        setPhone('')
        setStartsAt(
            initialDate
                ? initialDate.set({ hour: 10, minute: 0 }).toJSDate()
                : null,
        )
        setGivenById(workspaceUser?.id ?? '')
        setNotes('')
        setPreferredUnitConfig('')
        setPreferredMoveIn(null)
        setFieldErrors({})
        setSelectedGuestCardId('')
        setFormKey((k) => k + 1)
        resetGuestCardSubmit()
    }, [open, initialDate, workspaceUser?.id, resetGuestCardSubmit])

    const staffOptions = useMemo(() => {
        const byId = new Map<number, { id: number; name: string }>()
        for (const u of userList ?? []) {
            if (u?.id != null) {
                byId.set(u.id, {
                    id: u.id,
                    name: u.name || u.email || `User ${u.id}`,
                })
            }
        }
        if (workspaceUser?.id != null && !byId.has(workspaceUser.id)) {
            byId.set(workspaceUser.id, {
                id: workspaceUser.id,
                name:
                    workspaceUser.name ||
                    workspaceUser.email ||
                    `User ${workspaceUser.id}`,
            })
        }
        return Array.from(byId.values()).sort((a, b) =>
            a.name.localeCompare(b.name),
        )
    }, [userList, workspaceUser])

    const floorPlans = useMemo(() => {
        if (!unitConfigList) return []
        return unitConfigList
            .filter(
                (c: { shows_on_schedule?: boolean }) =>
                    c.shows_on_schedule !== false,
            )
            .sort((a: { name?: string }, b: { name?: string }) =>
                (a.name ?? '').localeCompare(b.name ?? ''),
            )
    }, [unitConfigList])

    const activeGuestCards = useMemo(
        () => (guestCards ?? []).filter((card) => !card.archived),
        [guestCards],
    )

    const selectedGuestCard: GuestCard | null = useMemo(() => {
        if (selectedGuestCardId === '') return null
        return (
            activeGuestCards.find((c) => c.id === selectedGuestCardId) ?? null
        )
    }, [activeGuestCards, selectedGuestCardId])

    useEffect(() => {
        if (step !== 'guest_card' || selectedGuestCardId !== '') return
        if (activeGuestCards.length === 1) {
            setSelectedGuestCardId(activeGuestCards[0].id)
            return
        }
        const defaultCard = activeGuestCards.find((c) => c.default)
        if (defaultCard) {
            setSelectedGuestCardId(defaultCard.id)
        }
    }, [step, activeGuestCards, selectedGuestCardId])

    const guestCardFormData = useMemo(() => {
        if (!selectedGuestCard) return null
        const unitConfigs = (
            unitConfigList.filter((config) => config.shows_on_schedule) || []
        ).map((config) => ({
            ...config,
            bed_count:
                (config as { bed_count?: number | null }).bed_count ?? null,
            bath_count:
                (config as { bath_count?: number | null }).bath_count ?? null,
        }))
        return {
            guest_card: selectedGuestCard,
            name: name.trim() || null,
            email: email.trim() || null,
            phone: phone.trim() || null,
            unit_configs: unitConfigs,
            apartment_name: '',
        }
    }, [selectedGuestCard, unitConfigList, name, email, phone])

    const validateTour = (): Record<string, string> => {
        const errors: Record<string, string> = {}
        if (!name.trim()) errors.name = 'Name is required'
        if (!email.trim() && !phone.trim())
            errors.contact = 'Email or phone is required'
        if (!startsAt) errors.startsAt = 'Tour date/time is required'
        return errors
    }

    const buildTourPayload = (overrides?: {
        name?: string
        email?: string
        phone?: string
        preferred_unit_config?: number | null
        preferred_move_in_date?: string | null
    }) => {
        const startsIso = DateTime.fromJSDate(startsAt!).toISO()!
        const endsIso = DateTime.fromJSDate(startsAt!)
            .plus({ hours: 1 })
            .toISO()!
        const resolvedName = (overrides?.name ?? name).trim()
        const resolvedEmail = (overrides?.email ?? email).trim()
        const resolvedPhone = (overrides?.phone ?? phone).trim()

        return {
            name: resolvedName,
            ...(resolvedEmail ? { email: resolvedEmail } : {}),
            ...(resolvedPhone ? { phone: resolvedPhone } : {}),
            starts_at: startsIso,
            ends_at: endsIso,
            given_by_id: givenById === '' ? null : givenById,
            notes: notes.trim() || undefined,
            preferred_unit_config:
                overrides?.preferred_unit_config !== undefined
                    ? overrides.preferred_unit_config
                    : preferredUnitConfig === ''
                    ? null
                    : preferredUnitConfig,
            preferred_move_in_date:
                overrides?.preferred_move_in_date !== undefined
                    ? overrides.preferred_move_in_date
                    : preferredMoveIn
                    ? DateTime.fromJSDate(preferredMoveIn).toFormat(
                          'yyyy-MM-dd',
                      )
                    : null,
        }
    }

    const handleScheduleOnly = async () => {
        const errors = validateTour()
        setFieldErrors(errors)
        if (Object.keys(errors).length > 0) {
            toast.error('Please fix the highlighted fields.')
            return
        }

        try {
            await createLeadWithTour(buildTourPayload())
            onClose()
        } catch {
            // error handled in hook
        }
    }

    const handleContinueToGuestCard = () => {
        const errors = validateTour()
        setFieldErrors(errors)
        if (Object.keys(errors).length > 0) {
            toast.error('Please fix the highlighted fields.')
            return
        }
        if (activeGuestCards.length === 0) {
            toast.error('No guest cards configured for this property')
            return
        }
        setIncludeGuestCard(true)
        setStep('guest_card')
        setFormKey((k) => k + 1)
    }

    const handleGuestCardChange = (cardId: number) => {
        setSelectedGuestCardId(cardId)
        setFormKey((k) => k + 1)
    }

    const handleGuestCardSubmit = async (formData: GuestCardFormRequest) => {
        if (!selectedGuestCard) {
            toast.error('Please select a guest card template')
            return
        }

        const errors = validateTour()
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors)
            setStep('tour')
            toast.error('Please fix the tour details first.')
            return
        }

        try {
            const created = await createLeadWithTour(
                buildTourPayload({
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    preferred_unit_config:
                        formData.floor_plan ??
                        (preferredUnitConfig === ''
                            ? null
                            : preferredUnitConfig),
                    preferred_move_in_date:
                        formData.preferred_move_in_date ??
                        (preferredMoveIn
                            ? DateTime.fromJSDate(preferredMoveIn).toFormat(
                                  'yyyy-MM-dd',
                              )
                            : null),
                }),
            )

            await submitLeadGuestCard({
                leadId: created.lead.id,
                payload: {
                    ...formData,
                    guest_card_id: selectedGuestCard.id,
                },
            })
            onClose()
        } catch {
            // errors handled in hooks
        }
    }

    const onGuestCardStep = step === 'guest_card'

    return (
        <Dialog
            open={open}
            onClose={isBusy ? undefined : onClose}
            maxWidth={false}
            PaperProps={{
                className: onGuestCardStep
                    ? classes.dialogPaperWide
                    : classes.dialogPaper,
            }}
        >
            <Box className={classes.title}>
                <Box>
                    <Typography variant="h6" style={{ fontWeight: 700 }}>
                        {onGuestCardStep ? 'Guest Card' : 'Schedule Tour'}
                    </Typography>
                    <Typography className={classes.stepHint}>
                        {onGuestCardStep
                            ? `For ${
                                  name.trim() || 'this prospect'
                              } · optional details before scheduling`
                            : 'Create a prospect and schedule their tour'}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={onClose} disabled={isBusy}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent className={classes.content}>
                {step === 'tour' && (
                    <>
                        <TextField
                            label="Name"
                            variant="outlined"
                            size="small"
                            fullWidth
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            error={!!fieldErrors.name}
                            helperText={fieldErrors.name}
                        />

                        <Box className={classes.row}>
                            <TextField
                                label="Email"
                                variant="outlined"
                                size="small"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                error={!!fieldErrors.contact}
                                helperText={
                                    fieldErrors.contact && !phone.trim()
                                        ? fieldErrors.contact
                                        : undefined
                                }
                            />
                            <TextField
                                label="Phone"
                                variant="outlined"
                                size="small"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                error={!!fieldErrors.contact && !email.trim()}
                            />
                        </Box>

                        <Box className={classes.row}>
                            <KeyboardDateTimePicker
                                label="Tour date & time"
                                format="MM/dd/yyyy hh:mm a"
                                inputVariant="outlined"
                                size="small"
                                value={startsAt}
                                onChange={(date) =>
                                    setStartsAt(date as Date | null)
                                }
                                required
                                error={!!fieldErrors.startsAt}
                                helperText={fieldErrors.startsAt}
                                style={{ flex: 1 }}
                            />
                            <FormControl
                                variant="outlined"
                                size="small"
                                style={{ flex: 1 }}
                            >
                                <InputLabel>Given by</InputLabel>
                                <Select
                                    value={givenById}
                                    onChange={(e) =>
                                        setGivenById(
                                            e.target.value as number | '',
                                        )
                                    }
                                    label="Given by"
                                >
                                    <MenuItem value="">
                                        <em>Unassigned</em>
                                    </MenuItem>
                                    {staffOptions.map((s) => (
                                        <MenuItem key={s.id} value={s.id}>
                                            {s.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box className={classes.row}>
                            <FormControl
                                variant="outlined"
                                size="small"
                                style={{ flex: 1 }}
                            >
                                <InputLabel>Preferred floor plan</InputLabel>
                                <Select
                                    value={preferredUnitConfig}
                                    onChange={(e) =>
                                        setPreferredUnitConfig(
                                            e.target.value as number | '',
                                        )
                                    }
                                    label="Preferred floor plan"
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {floorPlans.map(
                                        (fp: { id: number; name: string }) => (
                                            <MenuItem key={fp.id} value={fp.id}>
                                                {fp.name}
                                            </MenuItem>
                                        ),
                                    )}
                                </Select>
                            </FormControl>
                            <KeyboardDatePicker
                                label="Preferred move-in"
                                format="MM/dd/yyyy"
                                inputVariant="outlined"
                                size="small"
                                value={preferredMoveIn}
                                onChange={(date) =>
                                    setPreferredMoveIn(date as Date | null)
                                }
                                clearable
                                style={{ flex: 1 }}
                            />
                        </Box>

                        <TextField
                            label="Notes"
                            variant="outlined"
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                            rowsMax={4}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />

                        <Box className={classes.guestCardToggle}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={includeGuestCard}
                                        onChange={(e) =>
                                            setIncludeGuestCard(
                                                e.target.checked,
                                            )
                                        }
                                        color="primary"
                                        disabled={
                                            isLoadingGuestCards ||
                                            activeGuestCards.length === 0
                                        }
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            style={{ fontWeight: 600 }}
                                        >
                                            Also fill out a guest card
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                        >
                                            {isLoadingGuestCards
                                                ? 'Loading guest cards…'
                                                : activeGuestCards.length === 0
                                                ? 'No guest cards configured for this property'
                                                : 'Capture preferences and custom questions before scheduling'}
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>
                    </>
                )}

                {step === 'guest_card' && (
                    <>
                        {isLoadingGuestCards ? (
                            <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                py={6}
                            >
                                <CircularProgress size={28} />
                            </Box>
                        ) : (
                            <>
                                <Box className={classes.pickerCard}>
                                    <Typography
                                        variant="subtitle2"
                                        style={{ fontWeight: 700 }}
                                    >
                                        Guest card template
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        style={{ marginTop: 2 }}
                                    >
                                        Select a template, then complete the
                                        fields below.
                                    </Typography>
                                    <FormControl
                                        variant="outlined"
                                        size="small"
                                        className={classes.formControl}
                                    >
                                        <InputLabel id="schedule-guest-card-select-label">
                                            Guest Card
                                        </InputLabel>
                                        <Select
                                            labelId="schedule-guest-card-select-label"
                                            label="Guest Card"
                                            value={selectedGuestCardId}
                                            onChange={(e) =>
                                                handleGuestCardChange(
                                                    e.target.value as number,
                                                )
                                            }
                                            disabled={isBusy}
                                        >
                                            {activeGuestCards.map((card) => (
                                                <MenuItem
                                                    key={card.id}
                                                    value={card.id}
                                                >
                                                    {card.card_name}
                                                    {card.default
                                                        ? ' (default)'
                                                        : ''}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                {guestCardFormData ? (
                                    <GuestCardFormFields
                                        key={formKey}
                                        guestCardFormData={guestCardFormData}
                                        prospect={null}
                                        onSubmit={handleGuestCardSubmit}
                                        isLoading={isBusy}
                                        submitLabel="Schedule Tour & Submit Guest Card"
                                        loadingLabel="Scheduling..."
                                    />
                                ) : (
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        align="center"
                                    >
                                        Choose a guest card above to continue
                                    </Typography>
                                )}
                            </>
                        )}
                    </>
                )}
            </DialogContent>

            {step === 'tour' && (
                <DialogActions className={classes.actions}>
                    <Button
                        onClick={onClose}
                        disabled={isBusy}
                        style={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Cancel
                    </Button>
                    {includeGuestCard ? (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleContinueToGuestCard}
                            disabled={isBusy || isLoadingGuestCards}
                            style={{
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 8,
                            }}
                        >
                            Next: Guest Card
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleScheduleOnly}
                            disabled={isBusy}
                            style={{
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 8,
                                boxShadow: '0 2px 8px rgba(0, 140, 133, 0.25)',
                            }}
                        >
                            {isCreating ? 'Scheduling...' : 'Schedule Tour'}
                        </Button>
                    )}
                </DialogActions>
            )}

            {step === 'guest_card' && (
                <DialogActions className={classes.actions}>
                    <Button
                        onClick={() => setStep('tour')}
                        disabled={isBusy}
                        style={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Back
                    </Button>
                    <Button
                        onClick={handleScheduleOnly}
                        disabled={isBusy}
                        style={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Skip guest card
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    )
}
